import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Upload, LogOut, Calendar as CalIcon } from "lucide-react";

const EMPTY_TOUR = {
  slug: "", title_en: "", title_fr: "", short_desc_en: "", short_desc_fr: "",
  long_desc_en: "", long_desc_fr: "", duration: "", location: "",
  price: 0, currency: "EUR", hero_image: "", gallery_images: [],
  inclusions_en: [], inclusions_fr: [], is_active: true, order: 0,
};

const BOOKING_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
];

const BOOKING_STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  paid: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

function normalizeBookingStatus(status) {
  return status === "declined" ? "cancelled" : status || "pending";
}

function BookingStatusBadge({ status }) {
  const normalized = normalizeBookingStatus(status);
  const item = BOOKING_STATUSES.find((s) => s.value === normalized);
  return (
    <span className={`text-xs px-2 py-1 rounded ${BOOKING_STATUS_STYLES[normalized] || BOOKING_STATUS_STYLES.pending}`}>
      {item?.label || normalized}
    </span>
  );
}

function ImageUpload({ value, onChange, testid = "image-upload" }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value && <img src={imgUrl(value)} alt="" className="w-16 h-16 object-cover rounded border" />}
      <Input
        type="text"
        placeholder="https://… or upload"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testid}-url`}
      />
      <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testid}-btn`}>
        <Upload size={14} className="mr-1" /> {busy ? "…" : "Upload"}
      </Button>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

function ToursTab() {
  const [tours, setTours] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/tours");
    setTours(data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const payload = {
      ...editing,
      price: Number(editing.price) || 0,
      order: Number(editing.order) || 0,
      inclusions_en: typeof editing.inclusions_en === "string" ? editing.inclusions_en.split("\n").filter(Boolean) : editing.inclusions_en,
      inclusions_fr: typeof editing.inclusions_fr === "string" ? editing.inclusions_fr.split("\n").filter(Boolean) : editing.inclusions_fr,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/tours/${editing.id}`, payload);
      } else {
        await api.post("/admin/tours", payload);
      }
      toast.success("Saved");
      setOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this tour?")) return;
    await api.delete(`/admin/tours/${id}`);
    toast.success("Deleted");
    load();
  };

  const startNew = () => {
    setEditing({ ...EMPTY_TOUR });
    setOpen(true);
  };
  const startEdit = (t) => {
    setEditing({
      ...t,
      inclusions_en: (t.inclusions_en || []).join("\n"),
      inclusions_fr: (t.inclusions_fr || []).join("\n"),
    });
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Tours ({tours.length})</CardTitle>
        <Button onClick={startNew} data-testid="add-tour-btn"><Plus size={14} className="mr-1" /> New tour</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tours.map((t) => (
              <TableRow key={t.id} data-testid={`tour-row-${t.slug}`}>
                <TableCell>{t.hero_image && <img src={imgUrl(t.hero_image)} alt="" className="w-12 h-12 object-cover rounded" />}</TableCell>
                <TableCell className="font-medium">{t.title_en}</TableCell>
                <TableCell className="text-xs text-charcoal-400">{t.slug}</TableCell>
                <TableCell>€{t.price}</TableCell>
                <TableCell>{t.is_active ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(t)} data-testid={`edit-tour-${t.slug}`}><Edit2 size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(t.id)} data-testid={`del-tour-${t.slug}`}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit tour" : "New tour"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Slug</Label><Input data-testid="tour-slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
                <div><Label>Price (€)</Label><Input data-testid="tour-price" type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
                <div><Label>Duration</Label><Input value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} /></div>
                <div><Label>Location</Label><Input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
                <div><Label>Title (EN)</Label><Input data-testid="tour-title-en" value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} /></div>
                <div><Label>Title (FR)</Label><Input value={editing.title_fr} onChange={(e) => setEditing({ ...editing, title_fr: e.target.value })} /></div>
                <div><Label>Short desc (EN)</Label><Textarea rows={2} value={editing.short_desc_en} onChange={(e) => setEditing({ ...editing, short_desc_en: e.target.value })} /></div>
                <div><Label>Short desc (FR)</Label><Textarea rows={2} value={editing.short_desc_fr} onChange={(e) => setEditing({ ...editing, short_desc_fr: e.target.value })} /></div>
                <div className="col-span-2"><Label>Long description (EN)</Label><Textarea rows={4} value={editing.long_desc_en} onChange={(e) => setEditing({ ...editing, long_desc_en: e.target.value })} /></div>
                <div className="col-span-2"><Label>Long description (FR)</Label><Textarea rows={4} value={editing.long_desc_fr} onChange={(e) => setEditing({ ...editing, long_desc_fr: e.target.value })} /></div>
                <div><Label>Inclusions EN (one per line)</Label><Textarea rows={3} value={editing.inclusions_en} onChange={(e) => setEditing({ ...editing, inclusions_en: e.target.value })} /></div>
                <div><Label>Inclusions FR (one per line)</Label><Textarea rows={3} value={editing.inclusions_fr} onChange={(e) => setEditing({ ...editing, inclusions_fr: e.target.value })} /></div>
                <div><Label>Order</Label><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
                <div className="flex items-center gap-3 pt-6"><Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} /><span className="text-sm">Active</span></div>
              </div>
              <div><Label>Hero image</Label><ImageUpload value={editing.hero_image} onChange={(v) => setEditing({ ...editing, hero_image: v })} testid="tour-hero" /></div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} data-testid="save-tour-btn">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DatesTab() {
  const [tours, setTours] = useState([]);
  const [tourId, setTourId] = useState("");
  const [dates, setDates] = useState([]);
  const [selected, setSelected] = useState();

  useEffect(() => { api.get("/admin/tours").then((r) => { setTours(r.data || []); if (!tourId && r.data?.[0]) setTourId(r.data[0].id); }); }, []);
  const load = async () => {
    if (!tourId) return;
    const { data } = await api.get(`/admin/dates?tour_id=${tourId}`);
    setDates(data || []);
  };
  useEffect(() => { load(); }, [tourId]);

  const add = async () => {
    if (!selected || !tourId) return;
    const y = selected.getFullYear();
    const m = String(selected.getMonth() + 1).padStart(2, "0");
    const d = String(selected.getDate()).padStart(2, "0");
    await api.post("/admin/dates", { tour_id: tourId, date: `${y}-${m}-${d}` });
    toast.success("Date added");
    setSelected(undefined);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/dates/${id}`);
    load();
  };

  return (
    <Card>
      <CardHeader><CardTitle>Available dates</CardTitle></CardHeader>
      <CardContent>
        <div className="mb-4 max-w-sm">
          <Label>Tour</Label>
          <Select value={tourId} onValueChange={setTourId}>
            <SelectTrigger data-testid="dates-tour-select" className="mt-2"><SelectValue placeholder="Select a tour" /></SelectTrigger>
            <SelectContent>
              {tours.map((t) => <SelectItem key={t.id} value={t.id}>{t.title_en}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <Label>Pick a date to add</Label>
            <div className="mt-2 inline-block rounded-md border bg-white p-2">
              <Calendar mode="single" selected={selected} onSelect={setSelected} fromDate={new Date()} modifiersClassNames={{ selected: "bg-terracotta text-white" }} />
            </div>
            <Button className="mt-3" onClick={add} disabled={!selected} data-testid="add-date-btn"><Plus size={14} className="mr-1" /> Add date</Button>
          </div>
          <div>
            <Label>Existing dates ({dates.length})</Label>
            <div className="mt-2 space-y-2 max-h-[420px] overflow-y-auto">
              {dates.map((d) => (
                <div key={d.id} className="flex items-center justify-between bg-sand-100 px-3 py-2 rounded text-sm" data-testid={`date-row-${d.date}`}>
                  <span className="inline-flex items-center gap-2"><CalIcon size={14} /> {d.date}</span>
                  <Button size="sm" variant="ghost" onClick={() => remove(d.id)}><Trash2 size={14} /></Button>
                </div>
              ))}
              {dates.length === 0 && <p className="text-xs text-charcoal-400">No dates yet.</p>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BookingsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/bookings");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const updateBooking = async () => {
    if (!editing) return;
    await api.put(`/admin/bookings/${editing.id}`, { status: editing.status, payment_link: editing.payment_link });
    toast.success("Updated");
    setEditing(null);
    load();
  };

  return (
    <Card>
      <CardHeader><CardTitle>Booking requests ({items.length})</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Tour</TableHead>
              <TableHead>Guest</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Pax</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((b) => (
              <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                <TableCell>{b.date}</TableCell>
                <TableCell className="font-medium">{b.tour_title}</TableCell>
                <TableCell>{b.name}</TableCell>
                <TableCell className="text-xs"><div>{b.email}</div><div>{b.whatsapp}</div></TableCell>
                <TableCell>{b.guests}</TableCell>
                <TableCell><BookingStatusBadge status={b.status} /></TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => setEditing({ ...b, status: normalizeBookingStatus(b.status) })}>Manage</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Booking · {editing?.tour_title}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-sm space-y-1 text-charcoal-400">
                <p><strong>Guest:</strong> {editing.name} ({editing.guests} pax)</p>
                <p><strong>Date:</strong> {editing.date}</p>
                <p><strong>Email:</strong> {editing.email}</p>
                <p><strong>WhatsApp:</strong> <a href={`https://wa.me/${editing.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-terracotta underline">{editing.whatsapp}</a></p>
                {editing.message && <p><strong>Message:</strong> {editing.message}</p>}
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BOOKING_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment link (paste Stripe / PayPal link)</Label>
                <Input value={editing.payment_link || ""} onChange={(e) => setEditing({ ...editing, payment_link: e.target.value })} placeholder="https://stripe.com/…" />
              </div>
              <Button onClick={updateBooking} data-testid="save-booking-btn">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/gallery"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const payload = { image_url: editing.image_url, caption_en: editing.caption_en || "", caption_fr: editing.caption_fr || "", order: Number(editing.order) || 0 };
    if (editing.id) await api.put(`/admin/gallery/${editing.id}`, payload);
    else await api.post("/admin/gallery", payload);
    setEditing(null);
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/gallery/${id}`); load(); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Gallery ({items.length})</CardTitle>
        <Button onClick={() => setEditing({ image_url: "", caption_en: "", caption_fr: "", order: 0 })}><Plus size={14} className="mr-1" /> New photo</Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((g) => (
            <div key={g.id} className="relative group">
              <img src={imgUrl(g.image_url)} alt="" className="w-full aspect-square object-cover rounded" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center opacity-0 group-hover:opacity-100 gap-2">
                <Button size="sm" variant="secondary" onClick={() => setEditing({ ...g })}><Edit2 size={14}/></Button>
                <Button size="sm" variant="destructive" onClick={() => del(g.id)}><Trash2 size={14}/></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} photo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Image</Label><ImageUpload value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} testid="gallery-img" /></div>
              <div><Label>Caption (EN)</Label><Input value={editing.caption_en || ""} onChange={(e) => setEditing({ ...editing, caption_en: e.target.value })} /></div>
              <div><Label>Caption (FR)</Label><Input value={editing.caption_fr || ""} onChange={(e) => setEditing({ ...editing, caption_fr: e.target.value })} /></div>
              <div><Label>Order</Label><Input type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              <Button onClick={save} data-testid="save-gallery-btn">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/testimonials"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const payload = {
      name: editing.name || "",
      location: editing.location || "",
      content_en: editing.content_en || "",
      content_fr: editing.content_fr || "",
      avatar_url: editing.avatar_url || "",
      rating: Number(editing.rating) || 5,
      order: Number(editing.order) || 0,
    };
    if (editing.id) await api.put(`/admin/testimonials/${editing.id}`, payload);
    else await api.post("/admin/testimonials", payload);
    setEditing(null);
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Testimonials ({items.length})</CardTitle>
        <Button onClick={() => setEditing({ name: "", content_en: "", content_fr: "", rating: 5, order: items.length + 1 })}><Plus size={14} className="mr-1" /> New</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Location</TableHead><TableHead>Quote</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>{t.location}</TableCell>
                <TableCell className="text-xs text-charcoal-400 max-w-md truncate">{t.content_en}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...t })}><Edit2 size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(t.id)}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} testimonial</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                <div><Label>Location</Label><Input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
                <div><Label>Rating</Label><Input type="number" min={1} max={5} value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} /></div>
                <div><Label>Order</Label><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              </div>
              <div><Label>Content EN</Label><Textarea rows={3} value={editing.content_en} onChange={(e) => setEditing({ ...editing, content_en: e.target.value })} /></div>
              <div><Label>Content FR</Label><Textarea rows={3} value={editing.content_fr} onChange={(e) => setEditing({ ...editing, content_fr: e.target.value })} /></div>
              <Button onClick={save}>Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function BlogTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/blog"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const payload = {
      slug: editing.slug,
      title_en: editing.title_en || "",
      title_fr: editing.title_fr || "",
      excerpt_en: editing.excerpt_en || "",
      excerpt_fr: editing.excerpt_fr || "",
      content_en: editing.content_en || "",
      content_fr: editing.content_fr || "",
      cover_image: editing.cover_image || "",
      published: editing.published !== false,
      published_at: editing.published_at || new Date().toISOString(),
    };
    if (editing.id) await api.put(`/admin/blog/${editing.id}`, payload);
    else await api.post("/admin/blog", payload);
    setEditing(null);
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/blog/${id}`); load(); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Stories ({items.length})</CardTitle>
        <Button onClick={() => setEditing({ slug: "", title_en: "", title_fr: "", excerpt_en: "", excerpt_fr: "", content_en: "", content_fr: "", cover_image: "", published: true })}><Plus size={14} className="mr-1" /> New post</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Slug</TableHead><TableHead>Published</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.title_en}</TableCell>
                <TableCell className="text-xs">{p.slug}</TableCell>
                <TableCell>{p.published ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing({ ...p })}><Edit2 size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(p.id)}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} story</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
                <div className="flex items-center gap-3 pt-6"><Switch checked={editing.published !== false} onCheckedChange={(v) => setEditing({ ...editing, published: v })} /><span className="text-sm">Published</span></div>
                <div><Label>Title (EN)</Label><Input value={editing.title_en} onChange={(e) => setEditing({ ...editing, title_en: e.target.value })} /></div>
                <div><Label>Title (FR)</Label><Input value={editing.title_fr} onChange={(e) => setEditing({ ...editing, title_fr: e.target.value })} /></div>
                <div><Label>Excerpt (EN)</Label><Textarea rows={2} value={editing.excerpt_en} onChange={(e) => setEditing({ ...editing, excerpt_en: e.target.value })} /></div>
                <div><Label>Excerpt (FR)</Label><Textarea rows={2} value={editing.excerpt_fr} onChange={(e) => setEditing({ ...editing, excerpt_fr: e.target.value })} /></div>
                <div className="col-span-2"><Label>Content (EN)</Label><Textarea rows={6} value={editing.content_en} onChange={(e) => setEditing({ ...editing, content_en: e.target.value })} /></div>
                <div className="col-span-2"><Label>Content (FR)</Label><Textarea rows={6} value={editing.content_fr} onChange={(e) => setEditing({ ...editing, content_fr: e.target.value })} /></div>
              </div>
              <div><Label>Cover image</Label><ImageUpload value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="blog-cover" /></div>
              <Button onClick={save}>Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function FAQTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/faq"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const payload = {
      question_en: editing.question_en || "",
      question_fr: editing.question_fr || "",
      answer_en: editing.answer_en || "",
      answer_fr: editing.answer_fr || "",
      order: Number(editing.order) || 0,
    };
    if (editing.id) await api.put(`/admin/faq/${editing.id}`, payload);
    else await api.post("/admin/faq", payload);
    setEditing(null);
    load();
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/faq/${id}`); load(); };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>FAQ ({items.length})</CardTitle>
        <Button onClick={() => setEditing({ question_en: "", question_fr: "", answer_en: "", answer_fr: "", order: items.length + 1 })}><Plus size={14} className="mr-1" /> New</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((f) => (
            <div key={f.id} className="flex items-center justify-between bg-sand-100 px-4 py-3 rounded">
              <div>
                <p className="font-medium">{f.question_en}</p>
                <p className="text-xs text-charcoal-400">{f.answer_en}</p>
              </div>
              <div className="space-x-2">
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...f })}><Edit2 size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => del(f.id)}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit" : "New"} FAQ</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Question (EN)</Label><Input value={editing.question_en} onChange={(e) => setEditing({ ...editing, question_en: e.target.value })} /></div>
              <div><Label>Question (FR)</Label><Input value={editing.question_fr} onChange={(e) => setEditing({ ...editing, question_fr: e.target.value })} /></div>
              <div><Label>Answer (EN)</Label><Textarea rows={3} value={editing.answer_en} onChange={(e) => setEditing({ ...editing, answer_en: e.target.value })} /></div>
              <div><Label>Answer (FR)</Label><Textarea rows={3} value={editing.answer_fr} onChange={(e) => setEditing({ ...editing, answer_fr: e.target.value })} /></div>
              <div><Label>Order</Label><Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              <Button onClick={save}>Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SettingsTab() {
  const [s, setS] = useState({
    whatsapp_number: "", instagram_handle: "", email: "",
    tagline_en: "", tagline_fr: "",
    about_short_en: "", about_short_fr: "",
    about_long_en: "", about_long_fr: "",
  });
  useEffect(() => { api.get("/settings").then((r) => setS({ ...s, ...(r.data || {}) })); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    await api.put("/admin/settings", s);
    toast.success("Settings saved");
  };

  return (
    <Card>
      <CardHeader><CardTitle>Site settings</CardTitle></CardHeader>
      <CardContent className="space-y-4 max-w-3xl">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>WhatsApp number</Label><Input value={s.whatsapp_number} onChange={(e) => setS({ ...s, whatsapp_number: e.target.value })} /></div>
          <div><Label>Instagram handle (no @)</Label><Input value={s.instagram_handle} onChange={(e) => setS({ ...s, instagram_handle: e.target.value })} /></div>
          <div className="col-span-2"><Label>Public email</Label><Input value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} /></div>
          <div><Label>Tagline (EN)</Label><Input value={s.tagline_en} onChange={(e) => setS({ ...s, tagline_en: e.target.value })} /></div>
          <div><Label>Tagline (FR)</Label><Input value={s.tagline_fr} onChange={(e) => setS({ ...s, tagline_fr: e.target.value })} /></div>
          <div className="col-span-2"><Label>About short (EN)</Label><Textarea rows={3} value={s.about_short_en} onChange={(e) => setS({ ...s, about_short_en: e.target.value })} /></div>
          <div className="col-span-2"><Label>About short (FR)</Label><Textarea rows={3} value={s.about_short_fr} onChange={(e) => setS({ ...s, about_short_fr: e.target.value })} /></div>
          <div className="col-span-2"><Label>About long (EN)</Label><Textarea rows={5} value={s.about_long_en} onChange={(e) => setS({ ...s, about_long_en: e.target.value })} /></div>
          <div className="col-span-2"><Label>About long (FR)</Label><Textarea rows={5} value={s.about_long_fr} onChange={(e) => setS({ ...s, about_long_fr: e.target.value })} /></div>
        </div>
        <Button onClick={save} data-testid="save-settings-btn">Save settings</Button>
      </CardContent>
    </Card>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("tours");

  useEffect(() => {
    if (!loading && (!user || user === false)) {
      nav("/admin/login");
    }
  }, [user, loading, nav]);

  if (loading || !user) return <div className="pt-40 text-center text-charcoal-400">Loading…</div>;

  return (
    <div data-testid="admin-page" className="min-h-screen bg-sand-50 pt-24 pb-24 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-2">Admin</p>
            <h1 className="font-serif-display text-4xl text-charcoal">Khalfan dashboard</h1>
          </div>
          <Button variant="outline" onClick={() => { logout(); nav("/"); }} data-testid="admin-logout"><LogOut size={14} className="mr-1" /> Logout</Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="tours" data-testid="tab-tours">Tours</TabsTrigger>
            <TabsTrigger value="dates" data-testid="tab-dates">Dates</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Stories</TabsTrigger>
            <TabsTrigger value="faq" data-testid="tab-faq">FAQ</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="tours"><ToursTab /></TabsContent>
            <TabsContent value="dates"><DatesTab /></TabsContent>
            <TabsContent value="bookings"><BookingsTab /></TabsContent>
            <TabsContent value="gallery"><GalleryTab /></TabsContent>
            <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
            <TabsContent value="blog"><BlogTab /></TabsContent>
            <TabsContent value="faq"><FAQTab /></TabsContent>
            <TabsContent value="settings"><SettingsTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}
